import {
  IconBookOutline18,
  IconBracketsCurlyOutline18,
  IconClockOutline18,
  IconCodeOutline18,
  IconCubeOutline18,
  IconLayersOutline18,
  IconMapOutline18,
  IconPlugOutline18,
  IconRocketOutline18,
  IconRouteOutline18,
} from "nucleo-ui-outline-18";

import type { Icon } from "@mountayaapp/theme";

/**
 * @mountayaapp/docs curated navigation. The header tabs, sidebar order, grouping,
 * and icons are an explicit, hand-curated tree here rather than derived from
 * per-page frontmatter, so the information architecture reads the same regardless
 * of how pages were authored. Frontmatter still owns each page's `title` and
 * `description`; this module owns the shape and sequence of the chrome.
 */

/**
 * TabId names a top-level header tab. The header renders the tabs in `TABS`
 * order; the registry resolves a pathname to its owning tab; the shell reads the
 * tab's layout to decide whether a sidebar shows. A single union keeps the id set
 * closed so a typo cannot introduce a tab no config knows about.
 */
export type TabId =
  | "documentation"
  | "dataApi"
  | "mapStudioApi"
  | "tileApi"
  | "sdks"
  | "tools"
  | "changelog";

/**
 * TabLayout is how a tab frames its pages. `sidebar` draws the curated group rail
 * plus the page chrome (breadcrumbs, pager, table of contents); `bare` is a
 * full-width column with none of that chrome, used by the interactive explorers
 * and the changelog.
 */
export type TabLayout = "sidebar" | "bare";

/**
 * NavGroup is one collapsible section of the sidebar: the `tab` it belongs to, a
 * localized `labelKey` resolved against the `docs` namespace, a Nucleo `icon`
 * shown beside the heading, and the ordered, language-agnostic page `slugs` it
 * contains. A slug is the trailing-slash-free path a page is served at within a
 * language (`""` is the language root / overview); the registry resolves each slug
 * to a page in the active language, falling back to English, and drops slugs with
 * no page so the rendered sidebar never carries a dead link. The `tab` tag is what
 * lets the sidebar show only the active tab's groups.
 */
export interface NavGroup {
  icon: Icon;
  labelKey: string;
  slugs: string[];
  tab: TabId;
}

/**
 * Tab is one header tab: its `id`, the localized `labelKey` for its link, the
 * Nucleo `icon` beside that label, the `landingSlug` the link points at, an `owns`
 * predicate that decides which slugs route to this tab (for active-state and
 * layout), and the `layout` it frames its pages with. `TABS` order is reading
 * order left-to-right in the header's second row.
 */
export interface Tab {
  icon: Icon;
  id: TabId;
  labelKey: string;
  landingSlug: string;
  layout: TabLayout;
  owns: (slug: string) => boolean;
}

/**
 * NAVIGATION is the ordered sidebar tree across every tab. Group order and the
 * slug order within each group are the reading order of that section; the registry
 * preserves both when it resolves slugs to pages and filters groups to the active
 * tab.
 *
 * Three pages are intentionally absent: the GraphQL playground and the two
 * generated references. Each is a full-width explorer reached through its own
 * header tab, so listing it in a rail would offer a link that leaves the rail
 * behind. `isReachableSlug` knows this, and the discovery files gather them in
 * their own trailing section.
 *
 * The SDKs group is declared ahead of its content. Until a page resolves under
 * `sdks`, the registry drops the group's items and the header drops the tab, so
 * authoring `content/<lang>/sdks/**` is the only step that turns the section on.
 */
export const NAVIGATION: readonly NavGroup[] = [
  {
    icon: IconRocketOutline18,
    labelKey: "nav.group.startHere",
    slugs: ["", "quickstart", "authentication", "concepts", "errors"],
    tab: "documentation",
  },
  {
    icon: IconMapOutline18,
    labelKey: "nav.group.mapEmbedding",
    slugs: [
      "map-embedding",
      "map-embedding/quickstart",
      "map-embedding/parameters",
      "map-embedding/events",
      "map-embedding/examples",
    ],
    tab: "documentation",
  },
  {
    icon: IconRouteOutline18,
    labelKey: "nav.group.dataApi",
    slugs: [
      "data-api",
      "data-api/quickstart",
      "data-api/routing",
      "data-api/places",
      "data-api/itineraries",
      "data-api/analysis",
      "data-api/terrain",
    ],
    tab: "documentation",
  },
  {
    icon: IconCodeOutline18,
    labelKey: "nav.group.mapStudioApi",
    slugs: ["map-studio-api", "map-studio-api/quickstart"],
    tab: "documentation",
  },
  {
    icon: IconLayersOutline18,
    labelKey: "nav.group.tileApi",
    slugs: ["tile-api", "tile-api/quickstart", "tile-api/overlays", "tile-api/integration"],
    tab: "documentation",
  },
  {
    icon: IconCubeOutline18,
    labelKey: "nav.group.sdks",
    slugs: ["sdks"],
    tab: "sdks",
  },
  {
    icon: IconPlugOutline18,
    labelKey: "nav.group.tools",
    slugs: ["tools", "tools/mcp", "tools/ai-agents"],
    tab: "tools",
  },
  {
    icon: IconClockOutline18,
    labelKey: "nav.group.changelog",
    slugs: ["changelog"],
    tab: "changelog",
  },
];

/**
 * ownsSection builds an `owns` predicate over a section's root slugs: a slug
 * belongs to the tab when it is one of those roots or sits beneath it. Ownership
 * is by prefix rather than an explicit slug list so a page authored inside an
 * existing section routes to its tab without being registered twice.
 */
const ownsSection =
  (roots: readonly string[]) =>
  (slug: string): boolean =>
    roots.some((root) => slug === root || slug.startsWith(`${root}/`));

/**
 * DOCUMENTATION_TAB is the fallback tab every unclaimed slug routes to: its `owns`
 * answers true for everything, so a slug no other tab claims (the guides, the
 * landing page) belongs to it. It is named on its own, then placed first in
 * `TABS`, so `resolveTabId` can return it as a guaranteed-defined reference
 * without depending on array indexing.
 */
const DOCUMENTATION_TAB: Tab = {
  icon: IconBookOutline18,
  id: "documentation",
  labelKey: "nav.tab.documentation",
  landingSlug: "",
  layout: "sidebar",
  owns: () => true,
};

/**
 * TABS is the ordered header tab configuration and the single source of truth for
 * the tab information architecture. Documentation leads as the fallback and holds
 * everything read as prose: the entry path, Map Embedding, and each callable
 * surface's guides. The three interactive explorers follow as their own
 * destinations, then SDKs as the client libraries over those surfaces, then the AI
 * tooling built on them, then the changelog.
 *
 * The callable surfaces are always listed **Data API, Map Studio API, Tile API**,
 * here and in every card group and rail across the documentation. That order is the
 * product's own, not an alphabetical or size-based accident, so a reader meets the
 * three in the same sequence wherever they appear.
 *
 * Each explorer owns exactly ONE slug — its own page — rather than its product's
 * whole section. That is what keeps `data-api/routing` and the other guides in
 * Documentation, where they have a rail, while the explorer itself is framed bare
 * and renders edge-to-edge through its own `full: true` frontmatter.
 */
export const TABS: readonly Tab[] = [
  DOCUMENTATION_TAB,
  {
    icon: IconBracketsCurlyOutline18,
    id: "dataApi",
    labelKey: "nav.tab.dataApi",
    landingSlug: "data-api/playground",
    layout: "bare",
    owns: (slug) => slug === "data-api/playground",
  },
  {
    icon: IconCodeOutline18,
    id: "mapStudioApi",
    labelKey: "nav.tab.mapStudioApi",
    landingSlug: "map-studio-api/reference",
    layout: "bare",
    owns: (slug) => slug === "map-studio-api/reference",
  },
  {
    icon: IconLayersOutline18,
    id: "tileApi",
    labelKey: "nav.tab.tileApi",
    landingSlug: "tile-api/reference",
    layout: "bare",
    owns: (slug) => slug === "tile-api/reference",
  },
  {
    icon: IconCubeOutline18,
    id: "sdks",
    labelKey: "nav.tab.sdks",
    landingSlug: "sdks",
    layout: "sidebar",
    owns: ownsSection(["sdks"]),
  },
  {
    icon: IconPlugOutline18,
    id: "tools",
    labelKey: "nav.tab.tools",
    landingSlug: "tools",
    layout: "sidebar",
    owns: ownsSection(["tools"]),
  },
  {
    icon: IconClockOutline18,
    id: "changelog",
    labelKey: "nav.tab.changelog",
    landingSlug: "changelog",
    layout: "bare",
    owns: (slug) => slug === "changelog",
  },
];

/**
 * resolveTabId returns the tab that owns a slug. The non-fallback tabs are tested
 * in `TABS` order and the first whose `owns` matches wins; when none claims the
 * slug it belongs to Documentation, the catch-all. Centralizing the rule here
 * keeps the header, shell, and breadcrumbs from each re-deriving tab ownership.
 */
export const resolveTabId = (slug: string): Tab => {
  for (const tab of TABS) {
    if (tab.id !== "documentation" && tab.owns(slug)) {
      return tab;
    }
  }

  return DOCUMENTATION_TAB;
};
